import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Paper from 'apollo-react/components/Paper';
import Box from 'apollo-react/components/Box';
import StatusCheck from "apollo-react-icons/StatusCheck";
import { FormikDocumentUpload } from '../../../../common/FormikComponent';
import { useMultiForm } from '../../../../../contexts/MultiFormContext';
import FieldErrorMessage from '../../../../common/FieldErrorMessage';
// import axiosInstance from '../../../../../axiosInstance';
// import CommentsList from '../../../../common/FormApproveComponents/CommentsList/CommentsList';
// import CommentToggle from '../../../../common/CommentToggle/CommentToggle';

// component to sync Formik values to context when they change
const FormikContextSync = ({ onValuesChange }) => {
  const { values } = useFormikContext();
  const prevValuesRef = useRef(null);
  
  useEffect(() => {
    // only sync if values actually changed
    const valuesStr = JSON.stringify(values);
    if (prevValuesRef.current !== valuesStr) {
      prevValuesRef.current = valuesStr;
      onValuesChange(values);
    }
  }, [values, onValuesChange]);
  
  return null;
};

// Validation Schema
const validationSchema = Yup.object({
  ectd_file: Yup.array()
    .min(1, 'Please upload your eCTD document to continue')
    .max(1, 'You can only upload one eCTD file at a time')
    .required('An eCTD document is required for submission'),
});

const EctdUpload = () => {
  
  const { 
    ectdUpload,
    setFormDataWithValidation,
    errors,
    validity,
    // isSendbackMode = false,
  } = useMultiForm();

  // const [comments, setComments] = useState([]);

  // const fetchComments = useCallback(async () => {
  //   if (!ectdUpload?.ectd_file?.length) return;
  //   const response = await axiosInstance.get(`/api/ectd-comments/enduser/${ectdUpload?.ectd_file[0]?.id}`);
  //   setComments(response.data || []);
  // }, [ectdUpload]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   if (ectdUpload?.ectd_file?.length) {
  //     fetchComments();
  //   }
  // }, [ectdUpload, fetchComments]);

  // const handleAddComment = async (comment,subFormId, fileId = null, fileData = null) => {
  //   try {
  //     const requestData = {
  //       ectdFileId: ectdUpload?.ectd_file[0]?.id,
  //       comment,
  //     };

  //     // Add file ID if provided
  //     if (fileId) {
  //       requestData.fileId = fileId;
  //     }

  //     const response = await axiosInstance.post(`/api/ectd-comments/enduser`, requestData);

  //     // Enrich the response with file data if available
  //     const enrichedComment = { ...response.data };
  //     if (fileData && fileId) {
  //       // Convert fileData to the expected additional_file format
  //       enrichedComment.additional_file = {
  //         id: fileData.id,
  //         original_name: fileData.original_name,
  //         file_path: null, // Not provided by upload response
  //         file_size: fileData.file_size,
  //         mime_type: fileData.mime_type,
  //         createdAt: fileData.created_at,
  //         updatedAt: fileData.created_at,
  //         deletedAt: null,
  //         user_id: null, // Will be set by backend
  //         end_user_id: null, // Will be set by backend
  //         ...fileData // Include any additional fields
  //       };
  //     }

  //     console.log("Comment added with file data:", enrichedComment);
  //     setComments(prev => [...prev, enrichedComment]);
  //   } catch (error) {
  //     console.error("Error adding comment:", error);
  //     throw error;
  //   }
  // };

  // Track the last data sent to avoid validation loops
  const lastValidationDataRef = useRef(null);

  // Trigger validation when form is initialized with existing data
  useEffect(() => {
    if (ectdUpload && ectdUpload.ectd_file && ectdUpload.ectd_file.length > 0) {
      // Only trigger validation if the data has actually changed
      const currentDataString = JSON.stringify(ectdUpload);
      if (lastValidationDataRef.current !== currentDataString) {
        lastValidationDataRef.current = currentDataString;
        setFormDataWithValidation('ectdUpload', ectdUpload);
      }
    }
  }, [ectdUpload, setFormDataWithValidation]);

  // ref to track pending sync timeout
  const syncTimeoutRef = useRef(null);
  const lastSyncedValuesRef = useRef(null);

  // sync handler for FormikContextSync - handles all value changes
  const handleValuesSync = useCallback((values) => {
    // clear any pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    
    const currentValuesString = JSON.stringify(values);
    
    // skip if values haven't changed
    if (lastSyncedValuesRef.current === currentValuesString) {
      return;
    }
    
    // debounce regular input changes
    syncTimeoutRef.current = setTimeout(() => {
      if (lastSyncedValuesRef.current !== currentValuesString) {
        lastSyncedValuesRef.current = currentValuesString;
        setFormDataWithValidation('ectdUpload', values);
      }
    }, 150);
  }, [setFormDataWithValidation]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <div>
          <Formik
            initialValues={ectdUpload}
            validationSchema={validationSchema}
            enableReinitialize={true}
            validateOnChange={true}
            validateOnBlur={true}
          >
            {({ values, errors: formikErrors }) => {
              return (
                <Form>
                  {/* sync Formik values to context */}
                  <FormikContextSync onValuesChange={handleValuesSync} />
                  <Box sx={{ marginBottom: 4 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormikDocumentUpload
                          name="ectd_file"
                          label="eCTD Document"
                          accept=".zip,.rar,.tar,.gz,.7z"
                          helperText="Please select your eCTD document. Accepted file types: ZIP, RAR, TAR, GZ, or 7Z files"
                          multiple={false}
                          maxFiles={1}
                          required={true}
                        />
                                                  <FieldErrorMessage 
                            error={errors.ectdUpload?.fields?.ectd_file?.error} 
                          />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Status indicator */}
                  <Box
                    sx={{ 
                      display: "flex", 
                      alignItems: "center",
                      padding: 2,
                      backgroundColor: validity.ectdUpload ? '#e8f5e8' : '#f5f5f5',
                      borderRadius: 1,
                      border: validity.ectdUpload ? '1px solid #4caf50' : '1px solid #ddd',
                      marginBottom: 5
                    }}
                  >
                    {validity.ectdUpload && (
                      <StatusCheck size={20} color="#4caf50" style={{ marginRight: 8 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: validity.ectdUpload ? '#4caf50' : '#666',
                        fontWeight: validity.ectdUpload ? 'bold' : 'normal'
                      }}
                    >
                      {validity.ectdUpload 
                        ? '✓ Your eCTD document is ready for submission' 
                        : 'Please upload your eCTD document to continue'
                      }
                    </Typography>
                  </Box>
                  

                </Form>
              );
            }}
          </Formik>
          {/* Comments Section */}
          {/* <div 
            className="form-comment-section" 
            style={{ 
              isolation: 'isolate', 
              position: 'relative', 
              zIndex: 1,
              marginTop: '35px'
            }}
          >
            {
              isSendbackMode &&
              <CommentToggle
                onAddComment={handleAddComment}
                persona={"customer"}
                buttonText="Add Comment"
                buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
              />
            }
            <CommentsList
              prevComments={comments}
              pagination={{}}
              filters={{}}
              updateFilters={() => { }}
              showControls={false}
              showFilters={false}
              showSorting={false}
              showPageSize={false}
              showResultsInfo={false}
              showPagination={false}
            />
          </div> */}
        </div>
      </Grid>
    </Grid>
  );
};

export default EctdUpload;